import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import BuildingApplicationContent from './BuildingApplicationContent';
import ViewLogsModal from '../modals/ViewLogsModal';

const form = {
  keyword: '',
  status: '',
};

const statusOptions = [
  { label: 'ALL STATUS', value: '' },
  { label: 'PENDING', value: 'PENDING' },
  { label: 'POSTED', value: 'POSTED' },
  {
    label: 'FOR APPRAISAL AND ASSESSMENT',
    value: 'FOR APPRAISAL AND ASSESSMENT',
  },
  { label: 'FOR REASSESSMENT', value: 'FOR REASSESSMENT' },
  { label: 'FOR RESUBMISSION', value: 'FOR RESUBMISSION' },
  { label: 'PAID', value: 'PAID' },
  { label: 'REVERTED', value: 'REVERTED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

function BuildingApplicationContainer({
  match,
  history,
  getApplicationsList,
  applicationList,
  isLoading,
  applicationListPager,
  postApplication,
  cancelApplication,
  showData,
  revertApplication,
  ordinanceRevisionId
}) {
  const [formData, setFormData] = useState(form);
  const [filter, setFilter] = useState(form);
  const [toggleSearch, setToggleSearch] = useState(false);
  const setModal = useContext(ModalContext);

  const handleLogsModal = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <ViewLogsModal data={data} />,
      title: 'Building Application Logs',
      modalSize: 'modal-lg',
    });
  };

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    getApplicationsList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    [toggleSearch ? 'mother_tdn' : 'q']: formData.keyword,
    status: filter.status,
    sort_by: 'asc'
  };
  useEffect(() => {
    if (debouncedSearchTerm) {
      setFilter({
        ...filter,
        [toggleSearch ? 'mother_tdn' : 'q']: formData.keyword,
      });
      getApplicationsList(param);
    } else {
      setFormData([]);
      getApplicationsList({
        q: formData.keyword,
        status: filter.status
      });
    }
  }, [debouncedSearchTerm, toggleSearch]);

  const handleCancel = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to unpost this application?`,
      (remarks) => {
        cancelApplication(_.get(data, 'id'), { remarks }, () => {
          getApplicationsList();
          history.push(`/rpt/building/applications/${_.get(data, 'id')}`);
          showData(_.get(data, 'id'));
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      true
    );
  };

  const handlePost = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to post this application?`,
      () => {
        postApplication(_.get(data, 'id'), () => {
          getApplicationsList();
          history.push(`/rpt/building/applications/${_.get(data, 'id')}`);
          showData(_.get(data, 'id'));
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      false
    );
  };

  const handleRevert = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure you want to revert this application?`,
      () => {
        revertApplication(_.get(data, 'id'), () => {
          getApplicationsList();
          history.push(`/rpt/building/applications/${_.get(data, 'id')}`);
          showData(_.get(data, 'id'));
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      false
    );
  };

  const onSetStatusFilter = (key) => (e) => {
    setFilter({
      ...filter,
      [key]: e.value,
    });
    getApplicationsList({
      ...param,
      [key]: e.value, // status
    });
  };

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:applicationId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Building Applications"
            searchToggle={{
              label: 'Mother TDN',
              value: toggleSearch,
              toggle: setToggleSearch,
            }}
            getData={getApplicationsList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:applicationId?`}
            listName="ApplicationCodes"
            label="Building Applications"
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.tdn')}</div>
            )}
            setStatusFilter={onSetStatusFilter('status')}
            status={_.get(filter, 'status') || ''}
            statusOptions={statusOptions}
            statusFilter
            noRightCard
            isLoading={isLoading}
            selected={params.applicationId}
            data={applicationList}
            dataPager={applicationListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:applicationId?`}
                    render={() => (
                      <BuildingApplicationContent
                        handleLogsModal={handleLogsModal}
                        handlePost={handlePost}
                        handleCancel={handleCancel}
                        handleRevert={handleRevert}
                        ordinanceRevisionId={+ordinanceRevisionId}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:applicationId?`}
                  render={() =>
                    !params.applicationId && applicationList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(applicationList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

BuildingApplicationContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getApplicationsList: PropTypes.instanceOf(Function).isRequired,
  applicationList: PropTypes.instanceOf(Array).isRequired,
  applicationListPager: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  postApplication: PropTypes.instanceOf(Function).isRequired,
  cancelApplication: PropTypes.instanceOf(Function).isRequired,
  showData: PropTypes.instanceOf(Function).isRequired,
  revertApplication: PropTypes.instanceOf(Function).isRequired,
  ordinanceRevisionId: PropTypes.number.isRequired,
};

const mapStateToProps = ({ api }) => ({
  applicationList: _.get(api, `${c.GET_APPLICATIONS_LIST}.list`) || [],
  applicationListPager: _.get(api, `${c.GET_APPLICATIONS_LIST}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_APPLICATIONS_LIST) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(BuildingApplicationContainer);
